'use client';

import type { FC, RefObject } from 'react';

import type { TBoardLane } from '@/utils/auctions/auctionBoard';

import { AuctionBoardMoreTile } from './auction-board-more-tile';
import { AuctionBoardTile } from './auction-board-tile';

/**
 * One lane's row of tiles (spec 014, research R4).
 *
 * A list that scrolls sideways and snaps tile by tile, so a phone can swipe;
 * the arrows that page it live in the board's header, beside the lane switch,
 * and drive this row through `scrollRef`. The row's left edge sits on the
 * section's content edge like every other section on the page; the right
 * edge clips at the container, which is what says "there is more".
 *
 * The row's height is the tile's, fixed, so lanes swap without reflow.
 */
export const AuctionBoardRow: FC<{
  lane: TBoardLane;
  scrollRef: RefObject<HTMLDivElement | null>;
  /** Eager-load the first tile — only the default lane passes `true`. */
  priorityFirst?: boolean;
}> = ({ lane, scrollRef, priorityFirst = false }) => (
  <div ref={scrollRef} className="min-w-0 snap-x snap-mandatory overflow-x-auto overflow-y-hidden scrollbar-hide">
    <ul data-row="" className="flex w-max gap-3 py-2">
      {lane.tiles.map((tile, index) => (
        <li key={tile.key} className="shrink-0 snap-start">
          <AuctionBoardTile tile={tile} priority={priorityFirst && index === 0} />
        </li>
      ))}
      {lane.more && (
        <li key="more" className="shrink-0 snap-start">
          <AuctionBoardMoreTile lane={lane.kind} link={lane.more} />
        </li>
      )}
    </ul>
  </div>
);
